import React, { Fragment } from "react";
import { Menu, Transition } from "@headlessui/react";
import { shallow } from "zustand/shallow";

import useYardActionEvents from "@hsl/core/hooks/useYardAnalyticsEvents";
import cx from "@hsl/core/utils/cx";

import { Translate } from "../../hooks/useTranslation";
import useExplorerStore from "../../store/store";
import Button from "../Button";

const downloadIcon = (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        className="h-3.5 w-3.5"
    >
        <path
            d="M17,18.325l4.293-4.041a1.025,1.025,0,0,1,1.414,0,.944.944,0,0,1,0,1.367L16,22,9.293,15.65a.944.944,0,0,1,0-1.367,1.025,1.025,0,0,1,1.414,0L15,18.325V6.818A.923.923,0,0,1,16,6a.923.923,0,0,1,1,.818V18.325ZM7,24H25a3,3,0,0,1,0,6H7a3,3,0,0,1,0-6Zm9,2v2h9a1,1,0,0,0,0-2Z"
            transform="translate(-4 -6)"
        />
    </svg>
);

const DownloadDataButton = () => {
    const { downloadDataEvent } = useYardActionEvents();
    const {
        downloadCSVData,
        funds: filteredFunds,
        isFiltering,
        isActive: isSearching,
        loading,
    } = useExplorerStore(
        ({
            downloadCSVData,
            refinedData: { funds },
            isFiltering,
            search: { isActive },
            loading,
        }) => ({
            downloadCSVData,
            funds,
            isFiltering,
            isActive,
            loading,
        }),
        shallow,
    );
    const hasFilteredFunds =
        (isSearching || isFiltering) && filteredFunds.length > 0;

    return (
        <Menu as="div" className="relative inline-block text-left">
            <Menu.Button
                as={Button}
                icon={downloadIcon}
                className="ml-4 h-11 px-4 font-medium max-sm:hidden md:h-12"
                color="white"
            >
                <Translate>Download data</Translate>
            </Menu.Button>
            {!loading && (
                <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                >
                    <Menu.Items className="absolute right-0 z-10 mt-2 flex w-56 origin-top-right flex-col overflow-hidden rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                        <Menu.Item>
                            {({ active }) => (
                                <button
                                    className={cx(
                                        "appearance-none px-4 py-2 text-left text-sm",
                                        active && "bg-white-smoke",
                                    )}
                                    onClick={() => {
                                        downloadCSVData();
                                        downloadDataEvent({ selection: false });
                                    }}
                                >
                                    <Translate>All data</Translate>
                                </button>
                            )}
                        </Menu.Item>
                        <Menu.Item disabled={!hasFilteredFunds}>
                            {({ active, disabled }) => (
                                <button
                                    aria-disabled={disabled}
                                    className={cx(
                                        "appearance-none px-4 py-2 text-left text-sm",
                                        active && !disabled && "bg-white-smoke",
                                        disabled && "opacity-50",
                                    )}
                                    onClick={() => {
                                        if (hasFilteredFunds) {
                                            downloadCSVData(true);
                                            downloadDataEvent({
                                                selection: true,
                                            });
                                        }
                                    }}
                                >
                                    <Translate>Current selection</Translate>
                                </button>
                            )}
                        </Menu.Item>
                    </Menu.Items>
                </Transition>
            )}
        </Menu>
    );
};

export default DownloadDataButton;
